import { Injectable, Injector, ProviderToken, computed, inject, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

import { EntitlementService } from '../../core/tenancy/entitlement.service';
import { FloorStore } from '../../core/stores/floor.store';
import { OrdersStore, runningTableCount } from '../../core/stores/orders.store';
import { BillingStore } from '../../core/stores/billing.store';
import { TeamStore } from '../../core/stores/team.store';
import { InventoryStore } from '../../core/stores/inventory.store';
import { MenuStore } from '../../core/stores/menu.store';
import { parOf, stockLevelOf } from '../../shared/utils/stock-level.util';

/**
 * The header's per-route subtitle line — console redesign (Cafooze POS
 * prototype `subs`). Plain, friendly one-liners, several computed live from
 * the stores the page itself already reads.
 *
 * Stores that are NOT already alive app-wide (Team, Inventory, Menu, Billing) are
 * resolved lazily, only once their own route is showing — so the header
 * never opens a listener a signed-in role wouldn't otherwise have opened.
 * `untracked` keeps the store's own toSignal() construction out of this
 * computed's reactive context.
 */
@Injectable({ providedIn: 'root' })
export class PageSubtitleService {
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly entitlements = inject(EntitlementService);
  private readonly floor = inject(FloorStore);
  private readonly orders = inject(OrdersStore);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  private readonly segment = computed(() => this.url().split(/[?#]/)[0].split('/')[1] ?? '');

  private lazy<T>(token: ProviderToken<T>): T {
    return untracked(() => this.injector.get(token));
  }

  readonly subtitle = computed<string>(() => {
    switch (this.segment()) {
      case 'dashboard':
        return 'Your café at a glance';
      case 'tables': {
        const counts = this.floor.stateCounts();
        const total = this.floor.totalCount();
        return `${counts.occupied + counts.bill + counts.paidOnline} of ${total} tables in use`;
      }
      case 'orders': {
        // Same count as the page's cards: tables with a running order (isRunningOrder).
        const settled = new Set(
          this.lazy(BillingStore)
            .bills()
            .filter((b) => b.status !== 'draft')
            .map((b) => b.id)
        );
        const n = runningTableCount(this.orders.orders(), settled);
        return `${n} running order${n === 1 ? '' : 's'}`;
      }
      case 'billing':
        return "Take payments and see today's bills";
      case 'settings':
        return 'Set up your café the way you like';
      case 'tasks':
        return 'Jobs for your team and requests that need you';
      case 'team': {
        const team = this.lazy(TeamStore);
        return `${team.onShiftCount()} of ${team.activeEmployeeCount()} people on shift`;
      }
      case 'inventory': {
        // Same rule as the Stock page's Out + Running low cards (shared/utils/stock-level.util).
        const store = this.lazy(InventoryStore);
        const qtyById = new Map(store.stock().map((s) => [s.itemId, s.totalQuantity]));
        const n = store
          .stockItems()
          .filter((item) => item.active && stockLevelOf(qtyById.get(item.id) ?? 0, parOf(item)) !== 'ok').length;
        return n === 0 ? 'Everything is stocked up' : `${n} item${n === 1 ? ' needs' : 's need'} buying`;
      }
      case 'menu': {
        const items = this.lazy(MenuStore).items();
        // The Menu switch's own rule: isAvailable = active && !manualOutOfStock.
        const available = items.filter((i) => i.active && !i.manualOutOfStock).length;
        return `${available} of ${items.length} dishes available`;
      }
      case 'spend':
        return 'Purchases and running costs';
      case 'insights':
        return 'Sales, stock and how the day went';
      case 'marketing':
        return 'Bring guests back';
      default:
        return this.entitlements.venueName();
    }
  });
}
